/**
 * VariableSidebar Component
 *
 * Displays available template variables grouped by category.
 * Variables are clickable to insert them into the editor.
 *
 * @module components/claude-templates/VariableSidebar
 */

import { useState, useEffect, memo, useCallback } from 'react';
import { Variable, ChevronDown, ChevronRight, Loader2, AlertCircle, Copy, Check, Layers, Replace, PlusCircle } from 'lucide-react';

const API_BASE = '/api';

/**
 * Fetch available variables from backend
 */
async function fetchVariables() {
  const res = await fetch(`${API_BASE}/claude-templates/variables`, {
    credentials: 'include'
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to fetch variables');
  }
  return res.json();
}

/**
 * Default variables (fallback if API not available yet)
 */
const DEFAULT_VARIABLES = {
  agent: [
    { name: 'AGENT_NAME', description: 'Name of the agent' },
    { name: 'AGENT_DISPLAY_NAME', description: 'Display name of the agent' },
    { name: 'AGENT_UUID', description: 'Unique identifier of the agent' }
  ],
  project: [
    { name: 'PROJECT_NAME', description: 'Name of the project' },
    { name: 'PROJECT_DESCRIPTION', description: 'Project description' }
  ],
  owner: [
    { name: 'OWNER_NAME', description: 'Name of the project owner' },
    { name: 'OWNER_EMAIL', description: 'Email of the project owner' }
  ],
  role: [
    { name: 'ROLE_NAME', description: 'Name of the assigned role' },
    { name: 'ROLE_DISPLAY_NAME', description: 'Display name of the role' },
    { name: 'ROLE_LEVEL', description: 'Permission level of the role' }
  ],
  system: [
    { name: 'DATE', description: 'Current date (YYYY-MM-DD)' },
    { name: 'TIMESTAMP', description: 'Current timestamp (ISO 8601)' }
  ]
};

/**
 * Section markers for template inheritance
 */
const SECTION_MARKERS = [
  {
    name: 'replace',
    syntax: '<!-- replace -->',
    description: 'Add after ## header to replace parent section',
    icon: Replace,
    color: 'text-red-400'
  },
  {
    name: 'extend',
    syntax: '<!-- extend -->',
    description: 'Add after ## header to extend parent section',
    icon: PlusCircle,
    color: 'text-green-400'
  }
];

/**
 * Variable category display info
 */
const CATEGORY_INFO = {
  agent: { label: 'Agent', color: 'text-purple-400', bgColor: 'bg-purple-500/10' },
  project: { label: 'Project', color: 'text-blue-400', bgColor: 'bg-blue-500/10' },
  owner: { label: 'Owner', color: 'text-green-400', bgColor: 'bg-green-500/10' },
  role: { label: 'Role', color: 'text-amber-400', bgColor: 'bg-amber-500/10' },
  system: { label: 'System', color: 'text-gray-400', bgColor: 'bg-gray-500/10' }
};

/**
 * VariableItem component
 */
const VariableItem = memo(function VariableItem({ variable, onInsert }) {
  const [copied, setCopied] = useState(false);

  const handleClick = useCallback(() => {
    const varString = `{{${variable.name}}}`;
    onInsert(varString);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [variable.name, onInsert]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-full text-left px-3 py-2 hover:bg-gray-700/50 rounded-lg transition-colors group"
    >
      <div className="flex items-center justify-between">
        <code className="text-sm font-mono text-indigo-300">
          {`{{${variable.name}}}`}
        </code>
        {copied ? (
          <Check className="w-3.5 h-3.5 text-green-400" />
        ) : (
          <Copy className="w-3.5 h-3.5 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
        )}
      </div>
      {variable.description && (
        <p className="text-xs text-gray-500 mt-0.5">{variable.description}</p>
      )}
    </button>
  );
});

/**
 * SectionMarkerItem component
 */
const SectionMarkerItem = memo(function SectionMarkerItem({ marker, onInsert }) {
  const [copied, setCopied] = useState(false);
  const Icon = marker.icon;

  const handleClick = useCallback(() => {
    onInsert(marker.syntax);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [marker.syntax, onInsert]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-full text-left px-3 py-2 hover:bg-gray-700/50 rounded-lg transition-colors group"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={`w-3.5 h-3.5 ${marker.color}`} />
          <code className={`text-xs font-mono ${marker.color}`}>
            {marker.syntax}
          </code>
        </div>
        {copied ? (
          <Check className="w-3.5 h-3.5 text-green-400" />
        ) : (
          <Copy className="w-3.5 h-3.5 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
        )}
      </div>
      <p className="text-xs text-gray-500 mt-0.5 ml-5">{marker.description}</p>
    </button>
  );
});

/**
 * SectionMarkersPanel component
 */
const SectionMarkersPanel = memo(function SectionMarkersPanel({ onInsert, isExpanded, onToggle }) {
  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-700/30 transition-colors bg-orange-500/10"
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-orange-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-orange-400" />
        )}
        <Layers className="w-4 h-4 text-orange-400" />
        <span className="text-sm font-medium text-orange-400">Section Markers</span>
      </button>

      {isExpanded && (
        <div className="mt-1 ml-2">
          {SECTION_MARKERS.map((marker) => (
            <SectionMarkerItem
              key={marker.name}
              marker={marker}
              onInsert={onInsert}
            />
          ))}
          <div className="px-3 py-2 text-xs text-gray-500 italic">
            Usage: ## SectionName &lt;!-- replace --&gt;
          </div>
        </div>
      )}
    </div>
  );
});

/**
 * VariableCategory component
 */
const VariableCategory = memo(function VariableCategory({
  category,
  variables,
  isExpanded,
  onToggle,
  onInsert
}) {
  const info = CATEGORY_INFO[category] || { label: category, color: 'text-gray-400', bgColor: 'bg-gray-500/10' };

  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-700/30 transition-colors ${info.bgColor}`}
      >
        {isExpanded ? (
          <ChevronDown className={`w-4 h-4 ${info.color}`} />
        ) : (
          <ChevronRight className={`w-4 h-4 ${info.color}`} />
        )}
        <span className={`text-sm font-medium ${info.color}`}>
          {info.label}
        </span>
        <span className="text-xs text-gray-500">({variables.length})</span>
      </button>

      {isExpanded && (
        <div className="mt-1 ml-2">
          {variables.map((variable) => (
            <VariableItem
              key={variable.name}
              variable={variable}
              onInsert={onInsert}
            />
          ))}
        </div>
      )}
    </div>
  );
});

/**
 * VariableSidebar Component
 *
 * @param {object} props - Component props
 * @param {function} props.onInsert - Callback when variable is clicked (receives variable string like "{{VAR}}")
 * @param {string} [props.className] - Additional CSS classes
 */
const VariableSidebar = memo(function VariableSidebar({ onInsert, className = '' }) {
  const [variables, setVariables] = useState(DEFAULT_VARIABLES);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({
    sectionMarkers: true, // Section markers expanded by default
    agent: true,
    project: true,
    owner: false,
    role: false,
    system: false
  });

  // Fetch variables on mount
  useEffect(() => {
    setIsLoading(true);
    fetchVariables()
      .then(result => {
        if (result.data && typeof result.data === 'object') {
          setVariables(result.data);
        }
      })
      .catch(() => {
        // Silently use defaults if API not available
        setVariables(DEFAULT_VARIABLES);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const toggleCategory = useCallback((category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  }, []);

  const categories = Object.keys(variables);

  return (
    <div className={`flex flex-col h-full bg-gray-800/30 border-l border-gray-700 ${className}`}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <Variable className="w-4 h-4 text-indigo-400" />
          <span className="text-sm font-medium text-gray-200">Variables</span>
        </div>
        <p className="text-xs text-gray-500 mt-1">Click to insert at cursor</p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 text-indigo-400 animate-spin" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-8 text-center">
            <div>
              <AlertCircle className="w-6 h-6 mx-auto mb-2 text-red-400" />
              <p className="text-xs text-red-300">{error}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Section Markers - for inheritance control */}
            <SectionMarkersPanel
              onInsert={onInsert}
              isExpanded={expandedCategories.sectionMarkers ?? true}
              onToggle={() => toggleCategory('sectionMarkers')}
            />

            {/* Variable Categories */}
            {categories.map((category) => (
              <VariableCategory
                key={category}
                category={category}
                variables={variables[category] || []}
                isExpanded={expandedCategories[category] ?? true}
                onToggle={() => toggleCategory(category)}
                onInsert={onInsert}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
});

export default VariableSidebar;

export { DEFAULT_VARIABLES, CATEGORY_INFO, SECTION_MARKERS, SectionMarkersPanel };
